import { useAuth } from '../../auth/AuthContext';
import { LimitationEngine } from '../../utils/limitations';

interface GuitarLimitBadgeProps {
  currentCount: number;
  className?: string;
}

export function GuitarLimitBadge({ 
  currentCount, 
  className = ''
}: GuitarLimitBadgeProps) {
  const { user } = useAuth();
  const userTier = user?.tier || 'free';
  const limitCheck = LimitationEngine.checkGuitarLimit(currentCount, userTier);
  
  // Calculate progress percentage for color transitions
  const progressPercentage = Math.min((currentCount / limitCheck.limit) * 100, 100);
  const isNearLimit = progressPercentage > 66; // Show warning at 2/3
  const isAtLimit = !limitCheck.allowed;
  const isExactlyAtLimit = userTier === 'free' && currentCount === 4; // Red when exactly at 4/4

  const getBadgeColor = () => {
    if (userTier === 'premium') return '#00ff88'; // Green for premium
    if (isAtLimit) return '#ff4444'; // Red when over limit
    if (isExactlyAtLimit) return '#ff4444'; // Red when exactly at 4/4 limit
    if (isNearLimit) return '#ff8800'; // Orange when near limit
    return '#00ddff'; // Blue when safe
  };

  const getBadgeTitle = () => {
    if (userTier === 'premium') return 'Premium: Unlimited guitars';
    if (isAtLimit) return `Guitar limit exceeded (${currentCount}/${limitCheck.limit}). Upgrade for unlimited guitars!`;
    if (isExactlyAtLimit) return `Guitar limit reached (${currentCount}/${limitCheck.limit}). Upgrade for unlimited guitars!`;
    if (isNearLimit) return `Guitar limit warning (${currentCount}/${limitCheck.limit}). Consider upgrading for unlimited guitars.`;
    return `Guitar usage (${currentCount}/${limitCheck.limit})`;
  };

  return (
    <div 
      className={`guitar-limit-badge ${className}`}
      title={getBadgeTitle()}
      style={{
        backgroundColor: getBadgeColor(),
        borderColor: getBadgeColor()
      }}
    >
      {limitCheck.currentCount}/{userTier === 'premium' ? '∞' : limitCheck.limit}
    </div>
  );
}

// Enhanced styles with better typography and transitions
const styles = `
.guitar-limit-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #00ddff;
  border: 2px solid #00ddff;
  border-radius: 12px;
  color: #000;
  font-family: var(--font-primary);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  min-width: 24px;
  padding: 2px 6px;
  text-align: center;
  box-shadow: 0 0 8px rgba(0, 221, 255, 0.4);
  z-index: 10;
  transition: all 0.3s ease;
  text-shadow: none;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.guitar-limit-badge:hover {
  transform: scale(1.1);
  box-shadow: 0 0 12px rgba(0, 221, 255, 0.6);
}

/* Color variations are applied via inline styles */
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('guitar-limit-badge-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'guitar-limit-badge-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
